import { Box } from '@wegroup/design-system';
import React from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { PartyType } from '../../../../../../../../../types/SMT';
import { Option } from '../../../../../../../../design-system/src/content/components/SearchPickerInput';
import { InsurancePolicy } from '../../../../../../types/AdvisoryReport';
import { useChangeInsurancePolicyPackageOrder } from '../../../../api/packages/changeInsurancePolicyPackageOrder';
import useAdvisoryReportCreationParams from '../../../../hooks/useCreationFlowSubject';
import InsurancePolicyItem from '../../shared/insurance-policy/InsurancePolicyItem';

interface Props {
  insurancePolicies: InsurancePolicy[];
  partyType: PartyType;
  withActions?: boolean;
  customRiskObjects?: Option[];
  controlled?: boolean;
  hideAIBanner?: boolean;
  withDocuments?: boolean;

  // Actions
  onEdit?: (insurancePolicy: InsurancePolicy) => void;
  onRemove?: (id: string, packageId?: string) => void;
}

const InsurancePolicyPackagePolicies: React.FC<Props> = ({
  insurancePolicies,
  customRiskObjects = [],
  partyType,
  withActions,
  controlled,
  onEdit,
  onRemove,
  hideAIBanner,
  withDocuments,
}) => {
  const { reportId } = useAdvisoryReportCreationParams();
  const { mutateAsync: changePackageOrder } =
    useChangeInsurancePolicyPackageOrder();

  const reorder = (
    list: InsurancePolicy[],
    startIndex: number,
    endIndex: number,
  ) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const handleDragEnd = async (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const packageId = insurancePolicies?.[0]?.policy_package_id;
    const newOrder = reorder(
      insurancePolicies,
      result.source.index,
      result.destination.index,
    );

    if (packageId && reportId) {
      try {
        await changePackageOrder({
          reportId,
          packageId,
          insurance_policies: newOrder?.map(({ id }) => id),
        });
      } catch (err) {
        //
      }
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable
        droppableId="droppable"
        isDropDisabled={!withActions || controlled}
      >
        {(provided) => (
          <Box {...provided.droppableProps} ref={provided.innerRef}>
            {insurancePolicies.map((insurancePolicy, index) => (
              <InsurancePolicyItem
                key={insurancePolicy.id}
                insurancePolicy={insurancePolicy}
                partyType={partyType}
                index={index}
                withActions={withActions}
                customRiskObjects={customRiskObjects}
                onEdit={onEdit}
                onRemove={onRemove}
                controlled={controlled}
                hideAIBanner={hideAIBanner}
                defaultIsOpen={false}
                withDocuments={withDocuments}
              />
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default InsurancePolicyPackagePolicies;
